// src/pages/LoginPage.js
import { useState, useEffect } from "react"
import { useNavigate, Link } from "react-router-dom"
import { saveToken, isLoggedIn } from "../auth"
import { Clock, AlertCircle, Mail } from "lucide-react"

const LoginPage = () => {
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(false)
  const [isPendingApproval, setIsPendingApproval] = useState(false)
  const navigate = useNavigate()

  // Check if already logged in on mount AND whenever navigation occurs
  useEffect(() => {
    if (isLoggedIn()) {
      // Immediate redirect without rendering
      navigate("/analyzer", { replace: true })
    }
  }, [navigate])

  // Don't render the form if already logged in
  if (isLoggedIn()) {
    return null // Don't show anything while redirecting
  }

const handleLogin = async (e) => {
  e.preventDefault()
  setLoading(true)
  setError("")
  setIsPendingApproval(false)

  try {
    const response = await fetch("https://call-analyzer-backend.onrender.com/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    })

    const data = await response.json()

    if (response.ok && data.token) {
      // Check if user is approved
      if (data.status === "pending" || data.approved === false) {
        setIsPendingApproval(true)
        return
      }

      saveToken(data.token)
      // Direct redirect to analyzer - no subscription check needed
      navigate("/analyzer", { replace: true })
    } else {
      // Check if error is due to pending approval
      if (data.status === "pending" || data.detail?.toLowerCase().includes("pending")) {
        setIsPendingApproval(true)
      } else {
        setError(data.detail || "Login failed.")
      }
    }
  } catch (err) {
    console.error("Login error:", err)
    setError("Server error. Please try again.")
  } finally {
    setLoading(false)
  }
}

  // Show pending approval screen
  if (isPendingApproval) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-[#CED7DB] flex items-center justify-center px-4">
        <div className="max-w-md w-full">
          <div className="bg-white/80 backdrop-blur-sm p-8 rounded-xl shadow-lg border border-[#CED7DB]/30 text-center">
            <div className="mb-6">
              <div className="mx-auto w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mb-4">
                <Clock className="h-8 w-8 text-yellow-600" />
              </div>
              <h1 className="text-2xl font-bold text-[#0A4B4B] mb-2">Account Pending Approval</h1>
              <p className="text-gray-600">Your account is waiting for admin approval</p>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <div className="flex items-start">
                <AlertCircle className="h-5 w-5 text-yellow-600 mr-3 mt-0.5 flex-shrink-0" />
                <div className="text-left">
                  <h3 className="text-sm font-medium text-yellow-800">What does this mean?</h3>
                  <p className="text-sm text-yellow-700 mt-1">
                    Your registration is complete, but an administrator needs to approve your account before you can access the platform.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-center">
                <Mail className="h-5 w-5 text-blue-600 mr-2" />
                <span className="text-sm text-blue-700">
                  You'll receive an email when approved
                </span>
              </div>
            </div>

            <div className="space-y-3">
              <button
                onClick={() => setIsPendingApproval(false)}
                className="block w-full bg-[#0A4B4B] hover:bg-[#083d3d] text-white py-3 px-4 rounded-lg font-semibold transition-all duration-200 text-center"
              >
                Back to Login
              </button>
              <Link
                to="/"
                className="block text-sm text-gray-600 hover:text-[#0A4B4B] transition-colors"
              >
                ← Back to Home
              </Link>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-[#CED7DB] flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <img src="/New_Logo.png" alt="NeuroTrain Logo" className="mx-auto h-12 mb-4" />
          <h1 className="text-2xl font-bold text-[#0A4B4B] mb-2">Welcome Back</h1>
          <p className="text-gray-600">Sign in to your NeuroTrain account</p>
        </div>

        <div className="bg-white/80 backdrop-blur-sm p-8 rounded-xl shadow-lg border border-[#CED7DB]/30">
          <form onSubmit={handleLogin} className="space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <input
                id="email"
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0A4B4B] focus:border-[#0A4B4B] transition-colors"
                required
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <input
                id="password"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0A4B4B] focus:border-[#0A4B4B] transition-colors"
                required
              />
            </div>

            <div className="text-right">
              <Link
                to="/forgot-password"
                className="text-sm text-[#0A4B4B] hover:text-[#083d3d] font-medium transition-colors"
              >
                Forgot your password?
              </Link>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#0A4B4B] hover:bg-[#083d3d] disabled:bg-gray-400 text-white py-3 px-4 rounded-lg font-semibold transition-all duration-200 shadow-lg hover:shadow-xl disabled:cursor-not-allowed"
            >
              {loading ? "Signing In..." : "Sign In"}
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-gray-200">
            <p className="text-center text-sm text-gray-600">
              Don't have an account?{" "}
              <Link to="/register" className="text-[#0A4B4B] hover:text-[#083d3d] font-semibold transition-colors">
                Sign up here
              </Link>
            </p>
          </div>
        </div>

        <div className="text-center mt-6">
          <Link to="/" className="text-sm text-gray-600 hover:text-[#0A4B4B] transition-colors">
            ← Back to Home
          </Link>
        </div>
      </div>
    </div>
  )
}

export default LoginPage
